// Fange- (POW/EPW) og sivile-tap- (CIVCAS) registrering. Fange-håndtering følger
// «de 5 S-ene» (Search, Silence, Segregate, Speed, Safeguard) + humanitær
// behandling (Genève). CIVCAS logges for rapportering/ansvarlighet. Rene zod-
// skjema + hjelpere i samme mønster som casualty.ts — integritet håndheves i
// hubben (rapportør stemples ved fanout).
import { z } from 'zod';
// ── Fange (POW/EPW) ──────────────────────────────────────────────────────────
export const DetaineeCategory = z.enum(['epw', 'civilian-detainee', 'suspect', 'retained-personnel']);
export const DETAINEE_CATEGORY_LABEL = {
    epw: 'Krigsfange (EPW)',
    'civilian-detainee': 'Sivil internert',
    suspect: 'Mistenkt',
    'retained-personnel': 'Beskyttet personell (sanitet/prest)',
};
export const DetaineeStatus = z.enum(['detained', 'transferred', 'released', 'escaped', 'deceased']);
export const DETAINEE_STATUS_LABEL = {
    detained: 'I forvaring',
    transferred: 'Overført',
    released: 'Løslatt',
    escaped: 'Rømt',
    deceased: 'Omkommet',
};
/** De 5 S-ene for fange-håndtering (STANAG/feltmanual). */
export const FiveS = z.object({
    search: z.boolean().default(false), // Søk (våpen/etterretning)
    silence: z.boolean().default(false), // Stillhet (hindre samordning)
    segregate: z.boolean().default(false), // Segreger (grad/kjønn/status)
    speed: z.boolean().default(false), // Rask bakover-transport
    safeguard: z.boolean().default(false), // Sikre (beskytt mot skade)
});
export const DetaineeSchema = z.object({
    id: z.string().max(64),
    category: DetaineeCategory,
    status: DetaineeStatus.default('detained'),
    capturedBy: z.string().max(120).default(''),
    capturedAtMs: z.number(),
    lat: z.number().optional(),
    lon: z.number().optional(),
    /** «Capture tag» — hendelse ved pågripelse (hvem/hvor/omstendighet). */
    captureTag: z.string().max(2000).default(''),
    fiveS: FiveS.default({}),
    /** Antall (ved gruppe-registrering). */
    count: z.number().int().min(1).default(1),
    wounded: z.boolean().default(false),
    notes: z.string().max(4000).default(''),
    updatedMs: z.number().default(0),
});
export function parseDetainee(raw) {
    const p = DetaineeSchema.safeParse(raw);
    return p.success ? p.data : null;
}
/** Er alle 5 S-ene håndtert? (klar for bakover-transport). */
export function fiveSComplete(d) {
    const s = d.fiveS;
    return s.search && s.silence && s.segregate && s.speed && s.safeguard;
}
/** Hvilke S-er gjenstår (til sjekkliste/varsel). */
export function pendingFiveS(d) {
    const out = [];
    const s = d.fiveS;
    if (!s.search)
        out.push('Search');
    if (!s.silence)
        out.push('Silence');
    if (!s.segregate)
        out.push('Segregate');
    if (!s.speed)
        out.push('Speed');
    if (!s.safeguard)
        out.push('Safeguard');
    return out;
}
// ── Sivile tap (CIVCAS) ──────────────────────────────────────────────────────
export const CivCasCategory = z.enum(['killed', 'wounded', 'missing', 'displaced']);
export const CIVCAS_CATEGORY_LABEL = {
    killed: 'Drepte', wounded: 'Sårede', missing: 'Savnede', displaced: 'Fordrevne',
};
export const CivCasSchema = z.object({
    id: z.string().max(64),
    category: CivCasCategory,
    count: z.number().int().min(0).default(0),
    /** Antatt årsak (til ansvarlighet/etterforskning). */
    cause: z.string().max(500).default(''),
    lat: z.number().optional(),
    lon: z.number().optional(),
    reportedBy: z.string().max(120).default(''),
    tsMs: z.number(),
    notes: z.string().max(4000).default(''),
});
export function parseCivCas(raw) {
    const p = CivCasSchema.safeParse(raw);
    return p.success ? p.data : null;
}
/** Oppsummer sivile tap per kategori (til SITREP/dashbord). */
export function civCasRollup(items) {
    const out = { killed: 0, wounded: 0, missing: 0, displaced: 0 };
    for (const c of items)
        out[c.category] += c.count;
    return out;
}
//# sourceMappingURL=detainee.js.map